import { Canceler } from 'axios';
import { action, makeObservable, observable } from 'mobx';
import { ICustomerProfile } from '../../CustomerProfileEditScreen/types';
import { defaultCustomersProfilesFilters } from '../constants';
import { ICustomersProfilesFilters } from '../types';
import { defaultPagination } from 'src/constants/main';
import { SCREENS } from 'src/constants/screens';
import client from 'src/services/client';
import { history } from 'src/services/history';
import { IPagination, TBooleanInNumber } from 'src/types/main';
import { booleanNumberReverse, getCancelToken } from 'src/utils/helpers';
import { localeStore } from '~/mobx/localesStore';
import logger from '~/services/logger';
import { toastSuccess } from '~/utils/toasts';

class CustomersProfilesStore {
  constructor() {
    makeObservable(this);
  }

  cancelStack: {
    [key: string]: Canceler;
  } = {};

  @observable pagination: IPagination<ICustomerProfile> = defaultPagination;
  @observable filters: ICustomersProfilesFilters = defaultCustomersProfilesFilters;
  @action setFilters = (filters: Partial<ICustomersProfilesFilters>) => {
    this.filters = { ...this.filters, ...filters };
    this.fetchCustomersProfiles(1);
  };

  @action fetchCustomersProfiles = async (page: number = this.pagination.current_page + 1) => {
    try {
      const cancelId = 'customers-profiles-list';
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');
      this.pagination = { ...this.pagination, loading: true, refreshing: true };

      const pagination = await client.get('v3/crm/customers', {
        params: {
          page,
          search: this.filters.search,
          is_delete: 0,
          status: this.filters.status,
          sort_dir: this.filters.sortDir,
          sort_field: this.filters.sortField,
        },
        headers: { 'Accept-Language': 'en,ar' },
        cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)),
      });

      this.pagination = { ...pagination, loading: false, refreshing: false, data: pagination.data };
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination = { ...this.pagination, loading: false, refreshing: false };
      logger.error({ error, place: 'CustomersProfilesStore fetchCustomersProfiles' });
    }
  };

  @action changeStatus = async (id: number, status: TBooleanInNumber) => {
    try {
      const cancelId = `customer-status_${id}`;
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      this.pagination.data = this.pagination.data.map((item) => (item.id === id ? { ...item, status } : item));

      await client.put(`v3/crm/customers/${id}`, { status }, { cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)) });

      toastSuccess({ message: localeStore.t('toast_customer_profile_changes_saved') });
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination.data = this.pagination.data.map((item) => (item.id === id ? { ...item, status: booleanNumberReverse(item.status) } : item));
      logger.error({ error, place: 'CustomersProfilesStore changeStatus' });
    }
  };

  public clearFilters = () => {
    if (!history.location.pathname.includes(SCREENS.CRM())) this.filters = defaultCustomersProfilesFilters;
  };
}

export const customersProfilesStore = new CustomersProfilesStore();
